import { mergedRedirectSearch } from "./attribution";
import { hashedEnhancedConversionData } from "./enhancedConversionData";

const url = "https://data.bankradar.com/process-mortgage/internal/";

export function handleRedirection(redirectReason) {
  // Merged with sessionStorage attribution so ppcid/cid survive even when the
  // current URL no longer carries them — a bare-URL redirect books to the
  // organic default instead of the lane that paid for the click.
  let fullQueryString = mergedRedirectSearch();

  let redirectPartner = "ICB";
  let url = `https://ratezip.com/loanrates/rt-v2/${fullQueryString}`;

  if (redirectReason === "age") {
    // Under-62 goes to MRC (Paul, 2026-08-01) — reverses the brief 7/31
    // retarget to our /home-equity page.
    redirectPartner = "MRC";
    url = `https://www.ratezip.com/tables/mrc/${fullQueryString}`;
  }

  if (redirectReason === "state") {
    redirectPartner = "MRC";
    url = `https://www.ratezip.com/tables/mrc/${fullQueryString}`;
  }

  if (redirectReason === "homeValue") {
    redirectPartner = "MRC";
    url = `https://www.ratezip.com/tables/mrc/${fullQueryString}`;
  }

  if (redirectReason === "Longbridge offer") {
    window.dataLayer.push({
      event: "xltPixel",
    });

    redirectPartner = "Longbridge";
    url = `https://www.ratezip.com/rate-quotes/redirects/longbridge/${fullQueryString}`;
  }

  if (redirectReason === "ltv") {
    // High LTV means little equity left, which makes for a poor home-equity
    // lead — these go to MRC rather than diluting our own funnel's quality
    // (Paul, 2026-07-31).
    redirectPartner = "MRC";
    url = `https://www.ratezip.com/tables/mrc/${fullQueryString}`;
  }

  if (window.location.href.includes("/for-purchase")) {
    redirectPartner = "MRC";
    // mergedRedirectSearch() returns '' (not a bare '?') when there is
    // nothing to carry, so pick the right separator rather than assuming one.
    const qs = fullQueryString
      ? `${fullQueryString}&desc=purchase`
      : '?desc=purchase';
    url = `https://www.ratezip.com/tables/mrc/${qs}`;
  }

  window.dataLayer.push({
    redirect_partner: redirectPartner,
    redirect_reason: redirectReason,
    event: "userRedirect",
  });

  setTimeout(() => {
    window.location.replace(url || "http://ratezip.com/");
  }, 400);
}

export function buildFormData(state, jornaya, trustedForm, verification) {
  const data = {
    ppcid: state.ppcid,
    cid: state.cid,
    gclid: state.gclid,
    tsid: state.tsid,
    productname: "Reverse",
    zipcode: state.zipCode,
    city: state.city,
    state: state.state,
    addressone: state.address,
    firstname: state.firstName,
    lastname: state.lastName,
    balance: String(state.mortgageBalance > 2000000 ? 2000000 : state.mortgageBalance),
    ownerdob: "1940-04-15",
    ownerage: String(state.age),
    phonenumber: state.phoneNumber,
    emailaddress: state.email,
    propvalue: String(state.homeValue < 100000 ? 100000 : state.homeValue),
    trustedformcert: trustedForm,
    jornayatoken: jornaya,
    verificationstatus: verification,
    lander: state.isAppointment ? "guild-appointment" : "flyreverse-2",
    fullurl: window.location.href,
    militaryveteran: "No",
    creditrating: "Good",
    selfemployed: "No",
    propertytype: "Single Family Residence",
    return_buyer: "Yes"
  };

  if (window.location.href.toString().includes("localhost")) {
    data.test_lead = "Yes";
    data.verificationstatus = "Yes";
  }

  return data;
}

export async function submitWrapper(state, step, isVerified) {
  // This pilot is priced/routed for verified ReverseT3 leads. Do not let a
  // failed OTP fall through to the backend's unverified Peklava_Tier2 route.
  if (state.ppcid === '1000044' && !isVerified) {
    return { buyer: null, matched: false };
  }
  const { jornaya, trustedForm } = getCertValues();

  let verification = isVerified ? "Yes" : "No";

  if (window.location.href.includes("localhost")) {
    verification = "Yes";
  }

  const fd = buildFormData(state, jornaya, trustedForm, verification);
  let { salesValue, buyer, leadId, matched } = await makePostRequest(fd);

  if (window.location.href.includes("localhost")) {
    const params = new URLSearchParams(window.location.search);
    const mockBuyer = params.get("mock_buyer");
    if (mockBuyer) {
      buyer = mockBuyer;
      matched = true;
    }
  }

  // Hashed in-browser; raw PII never enters the dataLayer. {} on any failure.
  const ecUserData = await hashedEnhancedConversionData(
    state.email,
    state.phoneNumber
  );

  // cid fallback: cid can collide across two leads on one click; the boberdoo
  // lead_id can't, so it stays the primary order id.
  handleLeadSaleEvents(salesValue, step, leadId || state.cid, ecUserData);

  return { buyer, matched };
}

export async function makePostRequest(fd) {
  try {
    const response = await fetch(url, {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fd),
      redirect: "follow",
    });

    // A non-2xx body (422/500) carries no `status` key, so it must not be
    // mistaken for an unmatched-but-delivered lead.
    if (!response.ok) {
      return {
        salesValue: 0,
        buyer: null,
        leadId: null,
        matched: false,
      };
    }

    const result = await response.json();

    const { status, price, buyer, lead_id } = result;

    if (typeof status === "string" && status.toLowerCase() === "matched") {
      return {
        salesValue: +price,
        buyer: buyer,
        leadId: lead_id,
        matched: true,
      };
    }
    return {
      salesValue: 0,
      buyer: null,
      leadId: lead_id || null,
      matched: false,
    };
  } catch (error) {
    return {
      salesValue: 0,
      buyer: null,
      leadId: null,
      matched: false,
    };
  }
}

export function handleLeadSaleEvents(salesValue, step, leadOrderId, ecUserData) {
  if (+salesValue !== 0) {
    window.dataLayer.push({
      sale_price: salesValue,
      lead_order_id: leadOrderId,
      // uph_em / uph_ph — SHA-256-hashed user data for the GTM manual
      // user-provided-data variable (enhancedConversionData.js).
      ...(ecUserData || {}),
      event: "leadSale",
    });
    window.dataLayer.push({
      flow_step: step,
      flow_description: "Thank You - Sold Lead",
      event: "stepChange",
    });

    return;
  }

  window.dataLayer.push({
    sale_price: 0,
    event: "unsoldLead",
  });
  window.dataLayer.push({
    flow_step: step,
    flow_description: "Thank You - Unsold Lead",
    event: "stepChange",
  });
}

export function getCertValues() {
  const jornayaCertValue = document.getElementById("leadid_token").value;
  const trustedFormElementNodeList =
    document.getElementsByName("xxTrustedForm");

  const trustedFromCertValue = trustedFormElementNodeList[0]?.value || "";

  return {
    jornaya: jornayaCertValue,
    trustedForm: trustedFromCertValue,
  };
}
